import L from 'leaflet'
import Control from './control'
import rulerLegendControler from '../leaflet-legends/ruler-legend'
import {
  mdiTrashCanOutline,
  mdiAxisZRotateClockwise,
  mdiAxisYRotateClockwise,
  mdiArrowUpBoldBoxOutline,
  mdiHorizontalRotateCounterclockwise,
  mdiAlphaX,
  mdiAlphaY,
  mdiSegment,
  mdiPencil
} from '@mdi/js'
import SelectedMarkersControl from './selected-markers-control'
import { inside } from '../helpers/map-helpers'

export default Control.extend({
  _allMarkers: [],
  _legend: null,
  _selectedMarkersControl: null,
  options: {
    segments: 1,
    position: 'topright'
  },
  onAdd: function (map) {
    const vue = this.options.vue
    map.customControls[this.id] = this
    this._container = L.DomUtil.create('div')
    L.DomEvent.disableClickPropagation(this._container)
    L.DomEvent.on(this._map._container, 'keydown', this._toggleAction, this)
    L.DomEvent.on(this._map._container, 'keyup', this._toggleAction, this)

    this._allMarkers = L.layerGroup()
    this._allMarkers.addTo(this._map)

    Promise.all([
      vue.$axios.get(
        `${vue.$store.getters.projectPath}/runtime-markers/${this.options.layer.map_id}/${this.options.layer.map_name}/${this.options.layer.id}`
      ),
      vue.$axios.get(
        `${vue.$store.getters.projectPath}/segments`,
        { params: { level_name: this._map.options.currentMap } }
      )
    ]).then(async ([markers, segments]) => {
      this.options.segments = segments.data && segments.data.number_of_segments || 1
      markers.data.forEach(marker => {
        this._createMarkerOnTheMap(marker)
      })

      this._changeMapType({ target: map })
    }).catch(error => {
      console.error(error)
      vue.$toasted.error(error.response.data)
    })

    this._legend = new rulerLegendControler({
      vue: this.options.vue,
      defaultColor: this.options.defaultColor,
      parent: this
    })

    this._legend.addTo(this._map)

    map.on('zoomend', this._zoomend, this)
    map.on('change-type', this._changeMapType, this)
    map.on('click', this._clickOnMap, this)
    return this._container
  },
  _toggleAction: function (event) {
    if (event.shiftKey && event.type === "keydown") {
      this._map.eachLayer(layer => {
        if (layer instanceof L.Marker) {
          layer.setInteractive(false)
        }
      })
      this._map._container.classList.add('leaflet-shift')
      this._map.on('click', this._clicked, this);
    } else if (!event.shiftKey && event.type === "keyup") {
      this._map.eachLayer(layer => {
        if (layer instanceof L.Marker) {
          layer.setInteractive(true)
        }
      })
      this._map._container.classList.remove('leaflet-shift')
      this._map.off('click', this._clicked, this);
    }
  },
  _changeMapType: function (event) {
    if (this._selectedMarkersControl) {
      this._map.removeControl(this._selectedMarkersControl)
      this._selectedMarkersControl = null
    }

    const currentType = event.target.options.currentType
    this._allMarkers.eachLayer(layer => {
      if (layer.options.data.map_type == currentType) {
        event.target.addLayer(layer)
        this._updateMarkerCSS(layer)
      } else {
        event.target.removeLayer(layer)
      }
    })
  },
  _clicked: async function (event) {
    const layers = this._allMarkers.getLayers().filter(layer => layer.options.data.map_type === this._map.options.currentType)
    const marker = {
      options: {
        data: {
          name: null,
          segment_number: 1,
          x: event.latlng.lng,
          y: event.latlng.lat,
          z: 7000,
          yaw: 0,
          pitch: 0,
          roll: 0,
          layer_id: this.options.layer.id,
          map_type: this._map.options.currentType,
          order: layers.length
        }
      },
      updateMarker: this.createMarker.bind(this)
    }

    this.openSimpleDialog(marker, {
      title: 'Create Marker',
      rows: [
        {
          label: 'Name',
          key: 'name',
          type: 'string',
          component: 'text-field',
        },
        {
          label: 'Segment Number',
          key: 'segment_number',
          type: 'number',
          component: 'select',
          list: [...Array(this.options.segments)].map((segment, index) => index + 1),
        }
      ]
    })
  },
  _clickOnMap () {
    const menu = this._getSelectedMarkersMenu()
    if (menu) {
      this._map.removeControl(menu)
    }
  },
  createMarker: async function (info) {
    const vue = this.options.vue
    const { data } = await vue.$axios.post(
      `${vue.$store.getters.projectPath}/runtime-markers/${this.options.layer.map_id}/${this.options.layer.map_name}`,
      info
    ).catch(error => ({ data: { err: true, message: error.response.data } }))

    if (data.err) {
      vue.$toasted.error(data.message)
      return false
    } else {
      const marker = await this._createMarkerOnTheMap(data)
      this._openSelectedMarkerMenu({ target: marker })
      return true
    }
  },
  _createMarkerOnTheMap: async function (data) {
    let divCircle = null
    const span = document.createElement('span')
    span.innerText = data.segment_number
    span.style.color = this.options.defaultColor
    span.style.position = 'absolute'
    span.style.bottom = '-8px'
    span.style.right = '-4px'
    span.style.fontWeight = '600'
    span.style.fontSize = '12px'
    span.style.transform = `rotate(${(data.yaw + (this._map.options.mapConfig.angleOffset || 0)) * -1}deg)`

    if (this.options.layer.rotation) {
      divCircle = L.divIcon({
        html: span,
        className: 'chevron-up-circle-outline-point',
        iconSize: [32, 32],
        iconAnchor: [16, 16]
      })
    } else {
      divCircle = L.divIcon({
        html: span,
        className: 'record-circle-outline-point',
        iconSize: [32, 32],
        iconAnchor: [16, 16]
      })
    }

    const marker = L.marker([data.y, data.x], {
      icon: divCircle,
      selected: true,
      draggable: true,
      keyboard: false,
      rotationAngle: data.yaw + (this._map.options.mapConfig.angleOffset || 0),
      data,
      defaultColor: this.options.defaultColor,
      inactiveColor: this.options.inactiveColor,
      items: [
        {
          title: 'Remove',
          icon: mdiTrashCanOutline,
          twoLine: false,
          click: this.removeMarker.bind(this)
        },
        {
          title: 'Name',
          icon: mdiPencil,
          twoLine: true,
          subtitleKey: 'name',
          formInfo: {
            title: 'Set Name',
            rows: [
              {
                label: 'Name',
                key: 'name',
                type: 'string',
                component: 'text-field',
              }
            ]
          },
          click: this.openSimpleDialog.bind(this)
        },
        {
          title: 'Z-Index',
          icon: mdiArrowUpBoldBoxOutline,
          twoLine: true,
          subtitleKey: 'z',
          formInfo: {
            title: 'Set Z-Index',
            rows: [
              {
                label: 'Z-Index',
                key: 'z',
                type: 'number',
                component: 'text-field',
              }
            ]
          },
          click: this.openSimpleDialog.bind(this)
        },
        {
          title: 'Game X',
          icon: mdiAlphaX,
          twoLine: true,
          subtitleKey: 'gameX',
          formInfo: {
            title: 'Set Game X',
            rows: [
              {
                label: 'Game X',
                key: 'gameX',
                type: 'number',
                component: 'text-field',
              }
            ]
          },
          click: this.openSimpleDialog.bind(this)
        },
        {
          title: 'Game Y',
          icon: mdiAlphaY,
          twoLine: true,
          subtitleKey: 'gameY',
          formInfo: {
            title: 'Set Game Y',
            rows: [
              {
                label: 'Game Y',
                key: 'gameY',
                type: 'number',
                component: 'text-field',
              }
            ]
          },
          click: this.openSimpleDialog.bind(this)
        },
        {
          title: 'Segment',
          icon: mdiSegment,
          twoLine: true,
          subtitleKey: 'segment_number',
          formInfo: {
            title: 'Set Segment',
            rows: [
              {
                label: 'Segment Number',
                key: 'segment_number',
                type: 'number',
                component: 'select',
                list: [...Array(this.options.segments)].map((segment, index) => index + 1),
              }
            ]
          },
          click: this.openSimpleDialog.bind(this)
        }
      ]
    })

    if (this.options.layer.rotation) {
      marker.options.items.push({
        title: 'Yaw',
        icon: mdiAxisZRotateClockwise,
        twoLine: true,
        subtitleKey: 'yaw',
        edit: true,
        formInfo: {
          title: 'Set Yaw',
          rows: [
            {
              label: 'Yaw',
              key: 'yaw',
              type: 'number',
              component: 'text-field',
            }
          ]
        },
        click: this.rotateMarker.bind(this),
        subclick: this.openSimpleDialog.bind(this)
      })
      marker.options.items.push({
        title: 'Pitch',
        icon: mdiAxisYRotateClockwise,
        twoLine: true,
        subtitleKey: 'pitch',
        formInfo: {
          title: 'Set Pitch',
          rows: [
            {
              label: 'Pitch',
              key: 'pitch',
              type: 'number',
              component: 'text-field',
            }
          ]
        },
        click: this.openSimpleDialog.bind(this)
      })
      marker.options.items.push({
        title: 'Roll',
        icon: mdiHorizontalRotateCounterclockwise,
        twoLine: true,
        subtitleKey: 'roll',
        formInfo: {
          title: 'Set Roll',
          rows: [
            {
              label: 'Roll',
              key: 'roll',
              type: 'number',
              component: 'text-field',
            }
          ]
        },
        click: this.openSimpleDialog.bind(this)
      })
    }

    marker.updateMarker = this.updateMarker.bind(this)
    marker.removeMarker = this.removeMarker.bind(this)

    marker.on('add', () => {
      marker.on('dragend', this._dragend, this)
      marker.on('contextmenu', this._markerContextMenu, this)
      marker.on('click', this._openSelectedMarkerMenu, this)
    })

    marker.on('remove', () => {
      marker.off('dragend', this._dragend, this)
      marker.off('contextmenu', this._markerContextMenu, this)
      marker.off('click', this._openSelectedMarkerMenu, this)
    })

    marker.addTo(this._allMarkers)
    this._updateMarkerCSS(marker)
    return marker
  },
  _updateMarkerCSS: function (marker) {
    const element = marker.getElement()
    if (element) {
      marker.getElement().classList.add('shadow')
      marker.getElement().style.setProperty('--pseudo-backgroundcolor', this.options.defaultColor)
    }
  },
  _dragend: async function (event) {
    const target = event.target
    const latlng = target.getLatLng()

    if (this._getSelectedMarkersMenu()) {
      this._openSelectedMarkerMenu(event)
    }

    const [gameX, gameY] = this._map.convertPointToGameCoord(
      {
        x: latlng.lng,
        y: latlng.lat
      },
      target.options.data
    )
    target.options.data.gameX = gameX
    target.options.data.gameY = gameY

    await this.updateMarker({
      ...target.options.data,
      x: latlng.lng,
      y: latlng.lat
    }, event.target)
  },
  _markerContextMenu: function (event) {
    const target = event.target
    this.fire('marker-contextmenu', {
      ...event.containerPoint,
      data: target,
      items: target.options.items
    })
  },
  _openSelectedMarkerMenu: function (event) {
    const markers = []
    const marker = event.target
    const markerLatLng = marker.getLatLng()
    const [height, width] = marker.getIcon().options.iconSize

    const markerPoint = this._map.latLngToLayerPoint(markerLatLng)

    const boundsPoints = [
      L.point(markerPoint.x - width / 2, markerPoint.y - height / 2),
      L.point(markerPoint.x + width / 2, markerPoint.y - height / 2),
      L.point(markerPoint.x + width / 2, markerPoint.y + height / 2),
      L.point(markerPoint.x - width / 2, markerPoint.y + height / 2)
    ]

    const bounds = boundsPoints.map(point => this._map.layerPointToLatLng(point))

    let i = 0
    let selectedIndex = null
    this._map.eachLayer(layer => {
      if (layer instanceof L.Marker && inside(layer.getLatLng(), bounds)) {
        markers.push(layer)
        if (this._getSelectedMarkersMenu() && this._getSelectedMarkersMenu().isMarkerSelected(layer)) {
          selectedIndex = i
        } else if (layer._leaflet_id === marker._leaflet_id) {
          selectedIndex = i
        }
        i++
      }
    })

    if (markers.length && this._getSelectedMarkersMenu()) {
      this._getSelectedMarkersMenu().updateMarkersList(markers)
      this._getSelectedMarkersMenu().setInitMarker(marker)
      this._getSelectedMarkersMenu().setSelectedIndex(selectedIndex)
    } else if (markers.length) {
      new SelectedMarkersControl({
        vue: this.options.vue,
        _markers: markers
      }).addTo(this._map)

      this._getSelectedMarkersMenu().setInitMarker(marker)
      this._getSelectedMarkersMenu().setSelectedIndex(selectedIndex)
    }
  },
  removeMarker: async function (event) {
    const vue = this.options.vue
    const { data } = await vue.$axios.delete(
      `${vue.$store.getters.projectPath}/runtime-markers/${this.options.layer.map_name}/${this.options.layer.id}/${event.options.data.id}`
    ).catch(error => ({ data: { err: true, message: error.response.data } }))

    if (data.err) {
      vue.$toasted.error(data.message)
    } else {
      this._allMarkers.removeLayer(event)
      this._map.fire('marker-removed', event)

      let i = 0
      this._allMarkers.eachLayer(layer => {
        if (layer.options.data.map_type == this._map.options.currentType) {
          layer.options.data.order = i;
          i++;
        }
      })
    }
  },
  rotateMarker: function (event) {
    event.rotate = true
    this._map.off('click', this._clickOnMap, this);
    this._map.on('click', this._rotationClick, this);
    this._map.on('mousemove', this._rotation, this);
  },
  openSimpleDialog: function (marker, formInfo) {
    this.options.vue.openSimpleDialog(marker, formInfo)
  },
  _rotation: function (event) {
    this._allMarkers.eachLayer(layer => {
      if (layer.rotate) {
        if (layer.line_id) {
          this._allMarkers.getLayer(layer.line_id).setLatLngs([
            layer.getLatLng(),
            event.latlng
          ])
        } else {
          const line = L.polyline([
            layer.getLatLng(),
            event.latlng
          ], { dashArray: '3, 6', color: this.options.defaultColor, weight: 2 })

          this._allMarkers.addLayer(line)
          layer.line_id = line._leaflet_id
        }

        const center = layer.getLatLng()
        const cursor = event.latlng
        let yaw = (Math.atan2(cursor.lng - center.lng, cursor.lat - center.lat) * 180) / Math.PI
        yaw = yaw >= 0 ? yaw : 360 + yaw
        console.log(1)
        layer.setRotationAngle(yaw)
        layer.options.data.yaw = yaw - (this._map.options.mapConfig.angleOffset || 0).toFixed(2)
        if (layer.options.icon && layer.options.icon.options.html) {
          layer.options.icon.options.html.style.transform = `rotate(${yaw * -1}deg)`
        }
      }
    })
  },
  _rotationClick: function () {
    this._allMarkers.eachLayer(async layer => {
      if (layer.rotate) {
        this._allMarkers.removeLayer(this._allMarkers.getLayer(layer.line_id))
        layer.line_id = null
        await this.updateMarker(layer.options.data, layer)
      }
      layer.rotate = false
    })
    this._map.off('mousemove', this._rotation, this);
    this._map.off('click', this._rotationClick, this);
    this._map.on('click', this._clickOnMap, this);
  },
  updateMarker: async function (info, marker) {
    console.log(info)
    const vue = this.options.vue
    const { data } = await vue.$axios.put(
      `${vue.$store.getters.projectPath}/runtime-markers/${this.options.layer.map_id}/${this.options.layer.map_name}/${this.options.layer.id}/${info.id}`,
      info
    ).catch(error => ({ data: { err: true, message: error.response.data } }))

    if (data.err) {
      vue.$toasted.error(data.message)
      return false
    } else {
      const [ x, y ] = this._map.convertPointToLocalCoord(
        {
          x: Number(info.gameX),
          y: Number(info.gameY)
        },
        info
      )

      info.x = x
      info.y = y

      marker.options.data = { ...marker.options.data, ...info }

      const newLatLng = new L.LatLng(marker.options.data.y, marker.options.data.x)
      marker.setLatLng(newLatLng)
      marker.setRotationAngle(marker.options.data.yaw + (info.angleOffset || 0))

      if (marker.options.icon && marker.options.icon.options.html) {
        marker.options.icon.options.html.style.transform = `rotate(${(marker.options.data.yaw + (info.angleOffset || 0)) * -1}deg)`
      }

      return true
    }
  },
  _getSelectedMarkersMenu () {
    if ('selected-merkers' in this._map.customControls) {
      return this._map.customControls['selected-merkers']
    } else {
      return null
    }
  },
  _zoomend () {
    const marker = this._getSelectedMarkersMenu() && this._getSelectedMarkersMenu().getSelectedMarker() || null
    const initMarker = this._getSelectedMarkersMenu() && this._getSelectedMarkersMenu().getInitMarker() || null
    if (marker || initMarker) {
      this._openSelectedMarkerMenu({ target: marker || initMarker })
    }
  },
  onRemove: function (map) {
    if (this.options.vue.active === this.options.layer.id) {
      this.options.vue.active = null
    }

    this.off('marker-control')

    if (this._getSelectedMarkersMenu()) {
      this._map.removeControl(this._getSelectedMarkersMenu())
    }

    map.removeControl(this._legend)
    map.removeLayer(this._allMarkers)

    map.off('click', this._clickOnMap, this)
    map.off('zoomend', this._zoomend, this)
    map.off('change-type', this._changeMapType, this)

    L.DomEvent.off(this._map._container, 'keydown', this._toggleAction, this)
    L.DomEvent.off(this._map._container, 'keyup', this._toggleAction, this)
  }
})